import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Truck, Lock, ArrowRight, Eye, EyeSlash, Sparkle, ShieldCheck, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ROLE_META = {
  admin:      { title: "Admin",       tagline: "Full operations & finance control", accent: "#E50914" },
  dispatcher: { title: "Dispatcher",  tagline: "Live loads, drivers & tracking",    accent: "#F59E0B" },
  driver:     { title: "Driver",      tagline: "Mobile PWA · POD · settlements",     accent: "#0EA5E9" },
  customer:   { title: "Customer",    tagline: "Dispatch client portal · tracking · docs",   accent: "#10B981" },
};

export default function Login() {
  const { login, setSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(null);
  const [demoRoles, setDemoRoles] = useState([]);

  useEffect(() => {
    // Public list — never exposes real emails.
    api.get("/auth/demo/available")
      .then((r) => setDemoRoles(r.data.roles || []))
      .catch(() => setDemoRoles([]));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.challenge === "totp_required") {
      nav("/totp-challenge", {
        state: {
          challenge_token: r.challenge_token,
          user_email: r.user_email,
          user_role: r.user_role,
          recovery_codes_remaining: r.recovery_codes_remaining,
        },
      });
      return;
    }
    if (r.ok) {
      // Force password rotation for default-password users before any landing.
      if (r.user.must_change_password) {
        toast.message("Please set a new password to continue.");
        nav("/change-password-required");
        return;
      }
      // Policy: admin/dispatcher with required-2FA-on must enroll first.
      if (r.user.requires_2fa_enrollment) {
        toast.message("Two-factor authentication is required for your role. Please enroll to continue.");
        nav("/admin/security");
        return;
      }
      toast.success(`Welcome back, ${r.user.name}`);
      const role = r.user.role;
      if (role === "driver") nav("/driver");
      else if (role === "customer") nav("/customer");
      else if (role === "carrier") nav("/carrier");
      else nav("/admin");
    } else {
      toast.error(r.error);
    }
  };

  const launchDemo = async (role) => {
    setDemoBusy(role);
    try {
      const { data } = await api.post(`/auth/demo/${role}`);
      // Persist token via the AuthContext helper if available, otherwise fall
      // back to the standard token storage the api client understands.
      if (typeof setSession === "function") {
        await setSession(data.token, data.user);
      } else {
        localStorage.setItem("rf_token", data.token);
      }
      toast.success(`Welcome to the Roll Fast demo, ${data.user.name}`);
      const r = data.user.role;
      if (r === "driver") nav("/driver");
      else if (r === "customer") nav("/customer");
      else if (r === "carrier") nav("/carrier");
      else nav("/admin");
      // Nudge a full reload so AuthContext picks up the token in case
      // setSession isn't exposed. Small delay for the toast to render.
      setTimeout(() => {
        if (typeof setSession !== "function") window.location.reload();
      }, 200);
    } catch (e) {
      toast.error("Could not launch demo. Please try again.");
    } finally {
      setDemoBusy(null);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden"
         style={{ background: "#000000" }}>
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between gap-10 p-12 text-white relative">
        <div className="relative flex items-center gap-4">
          <img src="/logo.png?v=rf2026-newlogo-0804" alt="Roll Fast Solutions logo"
               className="w-16 h-16 object-contain" data-testid="brand-logo-hero" />
          <div>
            <div className="label-tag text-neutral-400 mb-0.5">Roll Fast</div>
            <div className="font-display font-black text-xl tracking-tight leading-none">ROLL FAST TMS</div>
          </div>
        </div>
        <div className="relative">
          <div className="label-tag text-neutral-500 mb-5">Nationwide Truck Dispatch for Owner-Operators &amp; Fleets</div>
          <h1 className="font-display font-black text-5xl md:text-6xl leading-[0.95] tracking-tighter mb-7">
            Keep your trucks<br/>
            <span style={{ color: "#E50914" }}>loaded.</span>
          </h1>
          <p className="text-neutral-400 max-w-md text-sm leading-[1.7]">
            Roll Fast is a nationwide truck-dispatch service for owner-operators and fleets. We book profitable loads, handle broker paperwork, and keep you moving — with a dedicated dispatcher who knows your operation.
          </p>
        </div>
        <div className="relative space-y-4">
          <div className="grid grid-cols-4 gap-5 text-neutral-300">
            <div><div className="font-mono text-2xl font-bold leading-none mb-1.5">U.S.</div><div className="label-tag text-neutral-500">Nationwide</div></div>
            <div><div className="font-mono text-2xl font-bold leading-none mb-1.5">24/7</div><div className="label-tag text-neutral-500">Support</div></div>
            <div><div className="font-mono text-2xl font-bold leading-none mb-1.5">1&nbsp;day</div><div className="label-tag text-neutral-500">Onboarding</div></div>
            <div><div className="font-mono text-2xl font-bold leading-none mb-1.5">★</div><div className="label-tag text-neutral-500">Dedicated Dispatcher</div></div>
          </div>
          <div className="pt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500" data-testid="hero-trust-line">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="bold" /> Secure cloud platform</span>
            <span className="text-neutral-700" aria-hidden="true">·</span>
            <span>Dedicated dispatch support</span>
            <span className="text-neutral-700" aria-hidden="true">·</span>
            <span>Nationwide service</span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 lg:p-12 text-white relative">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          {/* Mobile brand header */}
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/logo.png?v=rf2026-newlogo-0804" alt="Roll Fast Solutions logo"
                 className="w-12 h-12 object-contain" data-testid="brand-logo-mobile" />
            <div>
              <div className="label-tag text-neutral-500 mb-0.5">Roll Fast</div>
              <div className="font-display font-black text-xl tracking-tight leading-none">ROLL FAST TMS</div>
            </div>
          </div>
          <div className="label-tag text-neutral-500">Sign in</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2 text-white">Welcome back to Roll Fast</h2>
          <p className="text-sm text-neutral-400 mt-3 leading-relaxed">Sign in to your dispatcher, driver, or dispatch-client portal — or try a live demo below.</p>

          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="login-email" className="label-tag text-neutral-500">Email</label>
              <input id="login-email" data-testid="login-email-input" className="rf-input mt-1.5 w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label htmlFor="login-password" className="label-tag text-neutral-500">Password</label>
              <div className="relative mt-1.5">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  className="rf-input w-full pr-11"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-white focus:outline-none focus-visible:text-white transition-colors"
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={0}
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit-button" disabled={busy} type="submit"
              className="w-full justify-center mt-2 flex items-center gap-2 font-display font-bold py-3 px-4 rounded transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(229,9,20,0.4)] active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0"
              style={{ background: "#E50914", color: "#fff", boxShadow: "0 6px 18px rgba(229,9,20,0.35)" }}>
              <Lock size={16} weight="bold" /> {busy ? "Signing in…" : "Sign in"}
            </button>
          </div>

          <div className="mt-10">
            {demoRoles.length > 0 && (
              <div data-testid="demo-launch-section">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkle size={14} weight="fill" style={{ color: "#E50914" }} />
                  <span className="label-tag text-neutral-300">Explore the product · no signup</span>
                </div>
                <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
                  Instantly experience Roll Fast TMS from any perspective. Every demo is <span className="text-neutral-300">read-only</span> and isolated from real customer data.
                </p>
                <div className="grid grid-cols-2 gap-2.5" data-testid="demo-launch-grid">
                  {demoRoles.map((d) => {
                    const meta = ROLE_META[d.role] || { title: d.role, tagline: d.title, accent: "#E50914" };
                    const isBusy = demoBusy === d.role;
                    return (
                      <button
                        key={d.role}
                        type="button"
                        onClick={() => launchDemo(d.role)}
                        disabled={!!demoBusy}
                        data-testid={`demo-launch-${d.role}`}
                        className="group relative text-left px-3.5 py-3.5 rounded-md transition-all duration-150 disabled:opacity-60 hover:-translate-y-0.5 hover:border-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:ring-white/30"
                        style={{
                          background: "linear-gradient(135deg, #0f0f0f 0%, #131313 100%)",
                          border: "1px solid #262626",
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ background: meta.accent }} />
                              <div className="label-tag" style={{ color: meta.accent }}>
                                Explore as {meta.title}
                              </div>
                            </div>
                            <div className="text-xs text-neutral-400 mt-1 leading-snug">{meta.tagline}</div>
                          </div>
                          <div className="shrink-0 opacity-70 group-hover:opacity-100 transition">
                            {isBusy ? (
                              <span className="inline-block w-3.5 h-3.5 border-2 border-neutral-500 border-t-white rounded-full animate-spin" />
                            ) : (
                              <Play size={14} weight="fill" style={{ color: meta.accent }} />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-1.5 mt-3 text-[11px] text-neutral-500">
                  <ShieldCheck size={12} weight="bold" />
                  Demo sessions are sandboxed. No credentials, no PII, no writes.
                </div>
              </div>
            )}
            <div className="mt-4 text-right">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-neutral-500 hover:text-white transition-colors">
                Forgot password?
              </Link>
            </div>
          </div>

          {/* Primary CTA — Carrier Application (below the login section) */}
          <div className="mt-10 pt-6 border-t border-neutral-900" data-testid="carrier-cta-section">
            <div className="label-tag text-neutral-500 mb-1.5">New to Roll Fast?</div>
            <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
              Owner-operators &amp; small fleets — start booking loads with your own dispatcher this week.
            </p>
            <Link
              to="/carrier/signup"
              data-testid="carrier-application-button"
              className="w-full inline-flex items-center justify-center gap-2 font-display font-bold py-3 px-4 rounded transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(229,9,20,0.4)] active:translate-y-0"
              style={{ background: "#E50914", color: "#fff", boxShadow: "0 6px 18px rgba(229,9,20,0.35)" }}
            >
              <Truck size={16} weight="fill" />
              <span>Apply as a Carrier</span>
              <ArrowRight size={14} weight="bold" />
            </Link>
            <p className="text-[11px] text-neutral-600 mt-3 text-center leading-relaxed">
              5-minute application · No credit card · Talk to a dispatcher within 1 business day
            </p>
          </div>

          <div className="mt-10 pt-4 border-t border-neutral-900" data-testid="login-footer">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-neutral-400">
              <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <span className="text-neutral-700" aria-hidden="true">·</span>
              <Link to="/eula" data-testid="footer-eula-link" className="hover:text-white transition-colors">
                Terms / EULA
              </Link>
              <span className="text-neutral-700" aria-hidden="true">·</span>
              <Link to="/sms-policy" data-testid="footer-sms-link" className="hover:text-white transition-colors">
                SMS Policy
              </Link>
              <span className="text-neutral-700" aria-hidden="true">·</span>
              <Link to="/support" data-testid="footer-support-link" className="hover:text-white transition-colors">
                Support
              </Link>
            </div>
            <div className="mt-3 text-center text-[10px] text-neutral-600 font-mono">
              &copy; {new Date().getFullYear()} Roll Fast Solutions LLC · TMS
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
